import { supabase } from "@/integrations/supabase/client";
import { applyVisibleDocumentFilters } from "@/stores/documents/repository/queries";

export type ProjectActivityChannel = "documents" | "issues";

/**
 * A cheap fingerprint of a project's visible documents or issues: how many
 * there are, and the newest creation and modification. Any addition, deletion
 * or restore changes the count; a new row or an edit that stamps
 * `modified_at` (issues carry a trigger for it) moves a timestamp. Two
 * single-row reads per channel, so a portfolio of projects can poll it.
 */
export interface ProjectActivitySnapshot {
  revision: string;
}

function scopedQuery(
  orgId: string,
  projectId: number,
  channel: ProjectActivityChannel,
  columns: string
) {
  if (channel === "documents") {
    return applyVisibleDocumentFilters(
      supabase
        .from("documents")
        .select(columns, { count: "exact" })
        .eq("org_id", orgId)
        .eq("project_id", projectId)
    );
  }
  return supabase
    .from("pm_issues")
    .select(columns, { count: "exact" })
    .eq("org_id", orgId)
    .eq("building_project_id", projectId)
    .is("deleted_at", null);
}

/** RLS and the views' own visibility filters apply. */
export async function fetchProjectActivity(
  orgId: string,
  projectId: number,
  channel: ProjectActivityChannel
): Promise<ProjectActivitySnapshot> {
  const [created, modified] = await Promise.all([
    scopedQuery(orgId, projectId, channel, "created_at")
      .order("created_at", { ascending: false, nullsFirst: false })
      .limit(1),
    scopedQuery(orgId, projectId, channel, "modified_at")
      .order("modified_at", { ascending: false, nullsFirst: false })
      .limit(1),
  ]);
  if (created.error) throw new Error(created.error.message);
  if (modified.error) throw new Error(modified.error.message);
  const newestCreated =
    (created.data?.[0] as { created_at?: string | null } | undefined)?.created_at ?? "";
  const newestModified =
    (modified.data?.[0] as { modified_at?: string | null } | undefined)?.modified_at ?? "";
  return { revision: `${created.count ?? 0}|${newestCreated}|${newestModified}` };
}
